/**
 * Price Service
 *
 * Handles all price-related API operations for the Prices page.
 * This service uses MOCK data for now - backend endpoints not yet implemented.
 *
 * TODO: Replace mock data with real API calls when backend endpoints are ready:
 * - /prices/pending (GET)
 * - /prices/current (GET)
 * - /prices/history (GET)
 * - /prices/health (GET)
 * - /prices/approve (POST)
 * - /prices/reject (POST)
 * - /prices/rollback (POST)
 * - /prices/exceptions (GET)
 */

import type { ApiResponse } from '@/types/api.types'
import type {
  Price,
  PriceUpdate,
  PriceHistoryEntry,
  PriceHealthMetrics,
  PriceUpdateFilters,
  PriceFilters,
  HistoryFilters,
} from '@/pages/modules/PricesPage/types'

// Mock data for development
const MOCK_PRICE_UPDATES: PriceUpdate[] = [
  {
    uid: 'pu-1',
    productId: 'SKU-1234',
    productName: 'Premium Widget A',
    oldPrice: 99.99,
    newPrice: 109.99,
    oldMargin: 35.5,
    newMargin: 38.2,
    changePercent: 10.0,
    cost: 68.00,
    ruleUid: 'rule-1',
    ruleName: 'Standard 40% Markup',
    source: 'cost_change',
    status: 'pending',
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-123',
    version: 1,
  },
  {
    uid: 'pu-2',
    productId: 'SKU-5678',
    productName: 'Deluxe Gadget B',
    oldPrice: 149.99,
    newPrice: 139.99,
    oldMargin: 42.0,
    newMargin: 38.5,
    changePercent: -6.7,
    cost: 86.00,
    ruleUid: 'rule-2',
    ruleName: 'Competitive Pricing',
    source: 'rule_change',
    status: 'pending',
    createdAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-123',
    version: 1,
  },
  {
    uid: 'pu-3',
    productId: 'SKU-9012',
    productName: 'Basic Tool C',
    oldPrice: 29.99,
    newPrice: 45.99,
    oldMargin: 25.0,
    newMargin: 42.5,
    changePercent: 53.4,
    cost: 26.50,
    ruleUid: 'rule-1',
    ruleName: 'Standard 40% Markup',
    source: 'cost_change',
    status: 'exception',
    exceptionReason: 'Price increase exceeds 50% threshold - requires manual review',
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    createdBy: 'user-123',
    version: 1,
  },
]

const MOCK_CURRENT_PRICES: Price[] = [
  {
    uid: 'price-1',
    productId: 'SKU-1234',
    productName: 'Premium Widget A',
    currentPrice: 99.99,
    cost: 68.00,
    margin: 32.0,
    ruleUid: 'rule-1',
    ruleName: 'Standard 40% Markup',
    lastUpdated: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    effectiveDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    version: 5,
  },
  {
    uid: 'price-2',
    productId: 'SKU-5678',
    productName: 'Deluxe Gadget B',
    currentPrice: 149.99,
    cost: 86.00,
    margin: 42.7,
    ruleUid: 'rule-2',
    ruleName: 'Competitive Pricing',
    lastUpdated: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    effectiveDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
    version: 3,
  },
]

const MOCK_HISTORY: PriceHistoryEntry[] = [
  {
    uid: 'hist-1',
    action: 'bulk_approval',
    user: 'Doug Mitchell',
    userId: 'user-123',
    timestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    skusAffected: 47,
    avgPriceChange: 5.2,
    totalImpact: 2340.50,
    status: 'live',
    metadata: {
      vendorId: 'vendor-1',
    },
  },
  {
    uid: 'hist-2',
    action: 'rule_change',
    user: 'System',
    userId: 'system',
    timestamp: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    skusAffected: 123,
    avgPriceChange: -2.1,
    totalImpact: -1200.00,
    status: 'live',
    metadata: {
      ruleId: 'rule-2',
    },
  },
]

const MOCK_HEALTH_METRICS: PriceHealthMetrics = {
  totalSkus: 1247,
  pricedSkus: 1203,
  pendingApprovals: 3,
  exceptions: 1,
  stalePrices: 24,
  belowMinMargin: 8,
  avgMargin: 38.5,
  trends: {
    period: '30d',
    avgPriceChange: 3.2,
    topRisers: [
      { productId: 'SKU-9012', productName: 'Basic Tool C', change: 53.4 },
      { productId: 'SKU-1234', productName: 'Premium Widget A', change: 10.0 },
    ],
    topFallers: [
      { productId: 'SKU-5678', productName: 'Deluxe Gadget B', change: -6.7 },
    ],
  },
}

class PriceService {
  /**
   * Get pending price updates awaiting approval
   * TODO: Replace with real API call to /prices/pending
   */
  async getPendingUpdates(filters?: PriceUpdateFilters): Promise<ApiResponse<PriceUpdate[]>> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300))

    let filtered = [...MOCK_PRICE_UPDATES]

    // Apply filters
    if (filters?.status) {
      filtered = filtered.filter(u => u.status === filters.status)
    }

    return {
      success: true,
      data: filtered,
      meta: { total: filtered.length },
    }
  }

  /**
   * Get all active prices with filters
   * TODO: Replace with real API call to /prices/current
   */
  async getCurrentPrices(filters?: PriceFilters): Promise<ApiResponse<Price[]>> {
    await new Promise(resolve => setTimeout(resolve, 300))

    let filtered = [...MOCK_CURRENT_PRICES]

    // Apply search filter
    if (filters?.search) {
      const search = filters.search.toLowerCase()
      filtered = filtered.filter(
        p => p.productId.toLowerCase().includes(search) ||
             p.productName.toLowerCase().includes(search)
      )
    }

    return {
      success: true,
      data: filtered,
      meta: { total: filtered.length },
    }
  }

  /**
   * Get price change history
   * TODO: Replace with real API call to /prices/history
   */
  async getPriceHistory(_filters?: HistoryFilters): Promise<ApiResponse<PriceHistoryEntry[]>> {
    await new Promise(resolve => setTimeout(resolve, 300))

    return {
      success: true,
      data: MOCK_HISTORY,
      meta: { total: MOCK_HISTORY.length },
    }
  }

  /**
   * Get price health metrics for overview dashboard
   * TODO: Replace with real API call to /prices/health
   */
  async getPriceHealthMetrics(): Promise<ApiResponse<PriceHealthMetrics>> {
    await new Promise(resolve => setTimeout(resolve, 300))

    return {
      success: true,
      data: MOCK_HEALTH_METRICS,
    }
  }

  /**
   * Get price updates that need human review (exceptions)
   * TODO: Replace with real API call to /prices/exceptions
   */
  async getExceptions(): Promise<ApiResponse<PriceUpdate[]>> {
    await new Promise(resolve => setTimeout(resolve, 300))

    const exceptions = MOCK_PRICE_UPDATES.filter(u => u.status === 'exception')

    return {
      success: true,
      data: exceptions,
      meta: { total: exceptions.length },
    }
  }

  /**
   * Approve price updates (bulk or single)
   * TODO: Replace with real API call to /prices/approve
   */
  async approvePrices(
    updateIds: string[],
    _effectiveDate?: string
  ): Promise<ApiResponse<{ approved: number }>> {
    await new Promise(resolve => setTimeout(resolve, 500))

    // Mock: Remove approved items from pending list
    const approvedCount = updateIds.length

    return {
      success: true,
      data: { approved: approvedCount },
    }
  }

  /**
   * Reject price updates
   * TODO: Replace with real API call to /prices/reject
   */
  async rejectPrices(
    updateIds: string[],
    _reason?: string
  ): Promise<ApiResponse<{ rejected: number }>> {
    await new Promise(resolve => setTimeout(resolve, 500))

    return {
      success: true,
      data: { rejected: updateIds.length },
    }
  }

  /**
   * Rollback historical price change batch
   * TODO: Replace with real API call to /prices/rollback
   */
  async rollbackPriceChange(_historyEntryId: string): Promise<ApiResponse<{ rolledBack: number }>> {
    await new Promise(resolve => setTimeout(resolve, 500))

    return {
      success: true,
      data: { rolledBack: 47 }, // Mock value
    }
  }
}

export const priceService = new PriceService()
